import { Link } from "react-router-dom";

const PORTRAIT = "/images/anissa-portrait.jpg";
const HANDS = "https://customer-assets.emergentagent.com/job_skin-stillness/artifacts/olfepwyf_WillSalkeld_Amarna_April26_75.jpg";

const VALUES = [
  ["Kindness", "We move at your pace. Always."],
  ["Education", "We explain what we're doing and why."],
  ["Honesty", "No promises of perfection. No miracle claims."],
  ["Science", "Considered protocols, calmly applied."],
  ["Holistic wellbeing", "Skin, body, sleep, stress — all in the same conversation."],
  ["Gentleness", "On the skin, on the body, in the room."],
  ["Connection", "One woman at a time. We get to know each other."],
  ["Trust", "What is said in the room stays in the room."],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="pt-28 lg:pt-44 pb-20">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
          <div className="label-eyebrow">About</div>
          <h1 className="serif mt-7 text-[48px] sm:text-[80px] leading-[1] tracking-tight">
            A studio built from <span className="italic">listening</span>.
          </h1>
          <p className="mt-12 max-w-2xl text-[19px] leading-[1.8] text-[#2E2B27]">
            I'm Anissa — a skin therapist with over fifteen years' experience caring for skin. I created Studio A because I wanted somewhere women could be properly cared for, not processed.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7 img-zoom">
            <img src={PORTRAIT} alt="Anissa, founder of Studio A" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="lg:col-span-5 space-y-7 text-[17px] leading-[1.85] text-[#2E2B27]">
            <div className="label-eyebrow">Anissa</div>
            <p>I've worked in this industry for <strong>fifteen years</strong> — through editorial work and private client care. I've watched women be treated like timeslots more often than I'd like to admit. And for a long time, I treated my own skin the same way.</p>
            <p>What changed me was my own experience of <strong>stress and inflammation</strong>. The harder I pushed at the surface, the worse my skin got. It <strong>softened only when the rest of me did</strong>.</p>
            <p>Studio A is the appointment I used to wish I could book myself. <strong>Unhurried. Honest. Fully private.</strong> About the woman in front of me — not a script.</p>
            <p>If you've ever left a beauty appointment feeling smaller than when you arrived, I made this for you.</p>
            <p className="serif italic text-[20px] text-[#55524C]">— Anissa</p>
          </div>
        </div>
      </section>

      <section className="py-28">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
          <div className="label-eyebrow">The four meanings of A</div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              ["Anissa", "The hands and eye behind every treatment."],
              ["Art", "Skincare considered with the patience of a studio practice."],
              ["Awareness", "Of breath, body and the body in front of us."],
              ["Alignment", "Skin, nervous system and self — moving in the same direction."],
            ].map(([k, v]) => (
              <div key={k} className="border-t border-[#D5CDBF] pt-6">
                <div className="serif text-[36px] italic" style={{ color: "var(--terracotta)" }}>A</div>
                <h3 className="serif text-2xl mt-2">{k}</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-[#55524C]">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#EAE7E0]/60 py-32 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="label-eyebrow">How I work</div>
            <h2 className="serif mt-7 text-[34px] sm:text-[48px] leading-tight">One woman. All the time you need.</h2>
            <p className="mt-8 text-[17px] leading-[1.8] text-[#2E2B27] max-w-lg">
              <strong>No back-to-back bookings.</strong> No music chosen for ambience. <strong>We talk first, properly</strong> — and only then do I begin. You can be quiet, you can talk, you can fall asleep. All of it is welcome.
            </p>
            <Link to="/sanctuary" className="link-underline mt-10 inline-block text-[13px] uppercase tracking-[0.22em]">
              The Women's Sanctuary
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 img-zoom">
            <img src={HANDS} alt="" className="w-full aspect-[4/5] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-28">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
          <div className="label-eyebrow">What we hold to</div>
          <h2 className="serif mt-5 text-[32px] sm:text-[44px] leading-tight max-w-2xl">A short list of values — quietly held.</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-2">
            {VALUES.map(([k, v]) => (
              <div key={k} className="border-t border-[#D5CDBF] py-6">
                <h3 className="serif text-[22px] sm:text-[26px]">{k}</h3>
                <p className="mt-2 text-[14px] text-[#55524C] leading-relaxed">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="serif text-[40px] sm:text-[60px] leading-tight">When you're ready.</h2>
          <Link to="/book" data-testid="about-book-btn" className="btn-primary mt-12 inline-flex">
            Begin Your Ritual
          </Link>
        </div>
      </section>
    </div>
  );
}
